import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { getUserFriendlyError } from "@/lib/utils/error-messages";
import { trackAction, logger } from "@/lib/utils/logger";
import { pharmacySignup, npiLookup, resendVerification } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/auth-provider";
import { pharmacySignupResponseSchema } from "@/lib/validation/schemas";
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import {
  Loader2,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Shield,
  Clock,
  FileCheck,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CapsLockIndicator } from "@/components/forms";
import { useCapsLock } from "@/hooks";

export const Route = createFileRoute("/_auth/signup")({
  component: SignupPage,
});

type NPIData = {
  npi: string;
  legalName: string;
  dbaName: string | null;
  address: {
    street: string;
    street2: string | null;
    city: string;
    state: string;
    zipCode: string;
  };
  phone: string;
  fax: string | null;
  pharmacyType: string;
  stateLicense: string | null;
  licenseState: string;
  adminFirstName: string;
  adminLastName: string;
  adminPhone: string;
};

const HUBSPOT_API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

const DISPENSE_VOLUMES = [
  "1–10 per day",
  "11–20 per day",
  "21–30 per day",
  "30+ per day",
];

const TRACKING_METHODS = [
  "Paper logbook",
  "Spreadsheet",
  "Other software",
  "Not tracking yet",
];

const LOCATION_COUNTS = ["1", "2–3", "4–5", "6+"];

const REFERRAL_SOURCES = [
  "Google search",
  "LinkedIn",
  "Industry event",
  "Colleague / word of mouth",
  "Email",
  "Other",
];

function SignupPage() {
  const navigate = useNavigate();
  const { refetch: refetchAuth } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [autoLoginFailed, setAutoLoginFailed] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [_resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // HubSpot survey (success screen)
  const [surveyVolume, setSurveyVolume] = useState("");
  const [surveyTracking, setSurveyTracking] = useState("");
  const [surveyLocations, setSurveyLocations] = useState("");
  const [surveyReferral, setSurveyReferral] = useState("");
  const [surveySent, setSurveySent] = useState(false);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { isCapsLockOn, capsLockProps } = useCapsLock();

  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Step content
  const stepContent = {
    1: {
      title: "Create Your Account",
      description: "Set up your pharmacy's login credentials",
    },
    2: {
      title: "Verify Your Pharmacy",
      description: "Enter your NPI to auto-fill pharmacy details",
    },
    3: {
      title: "Pharmacy Information",
      description: "Review and complete your pharmacy profile",
    },
    4: {
      title: "Review & Confirm",
      description: "Double-check your information before submitting",
    },
  };

  // Step 1: Email & Password
  const [adminEmail, setAdminEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Step 2: NPI
  const [npi, setNpi] = useState("");
  const [_npiData, setNpiData] = useState<NPIData | null>(null);
  const [npiLoading, setNpiLoading] = useState(false);

  // Step 3: Pharmacy Details
  const [legalName, setLegalName] = useState("");
  const [dbaName, setDbaName] = useState("");
  const [addressStreet, setAddressStreet] = useState("");
  const [addressStreet2, setAddressStreet2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");
  const [fax, setFax] = useState("");
  const [stateLicense, setStateLicense] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [pharmacyType, setPharmacyType] = useState("");

  // Admin info from NPI (for first user creation later)
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");

  const handleNpiLookup = async () => {
    setError("");
    setNpiLoading(true);

    try {
      const result = await npiLookup(npi);
      const data = (result as { data: NPIData }).data;

      setNpiData(data);

      // Auto-populate fields
      setLegalName(data.legalName);
      setDbaName(data.dbaName || "");
      setAddressStreet(data.address.street);
      setAddressStreet2(data.address.street2 || "");
      setCity(data.address.city);
      setState(data.address.state);
      setZipCode(data.address.zipCode);
      setPhone(data.phone);
      setFax(data.fax || "");
      setPharmacyType(data.pharmacyType);
      setStateLicense(data.stateLicense || "");
      setLicenseState(data.licenseState);

      // Store admin name for first user creation
      setAdminFirstName(data.adminFirstName || "");
      setAdminLastName(data.adminLastName || "");

      // Auto-advance to next step
      setCurrentStep(3);
    } catch (err) {
      const errData = err as { error?: string };
      setError(errData.error || "Failed to lookup NPI");
      logger.error("NPI lookup failed", err, {
        operation: "npi_lookup",
        metadata: { npi },
      });
    } finally {
      setNpiLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError("");
    setIsLoading(true);

    trackAction("signup_started", "ui", { npi });

    try {
      const data = await pharmacySignup({
        password,
        npi,
        legalName,
        dbaName,
        address: {
          street: addressStreet,
          street2: addressStreet2,
          city,
          state,
          zipCode,
        },
        phone,
        fax,
        stateLicense,
        licenseState,
        adminEmail,
        timezone,
        pharmacyType,
        adminFirstName,
        adminLastName,
      });

      trackAction("signup_success", "ui");

      const parsed = pharmacySignupResponseSchema.safeParse(data);
      if (!parsed.success) {
        logger.error("Signup response validation failed", parsed.error, {
          operation: "signup",
          metadata: { npi },
        });
        // Backend reported success but the shape is unexpected — fall back
        // to the manual login path so the user isn't stranded.
        setError(
          "We created your account, but something looks off. Please log in to continue."
        );
        return;
      }

      const didAutoLoginFail = parsed.data.autoLoginFailed === true;
      setSuccess(true);
      setUserEmail(parsed.data.data.email);
      setAutoLoginFailed(didAutoLoginFail);

      // Backend auto-logs the user in by issuing pharmacy session cookies.
      // Refetch auth context so the success-screen CTA can drop them straight
      // into the app (where the create-first-user modal will prompt them).
      if (!didAutoLoginFail) {
        refetchAuth().catch((err) => {
          console.warn("Auth refetch failed after signup:", err);
        });
      }
    } catch (err) {
      // Log full error details to console for debugging
      console.error("Signup error:", err);

      // Show user-friendly error message
      const errorMessage = getUserFriendlyError(
        err,
        "Signup failed. Please try again."
      );
      setError(errorMessage);
      logger.error("Signup failed", err, {
        operation: "signup",
        metadata: { npi },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep1 = () => {
    const errors: Record<string, string> = {};

    if (!adminEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
      errors.adminEmail = "Valid email address is required";
    }
    if (!password || password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(password)) {
      errors.password = "Password must include at least one uppercase letter";
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted fields");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const errors: Record<string, string> = {};

    if (!/^\d{10}$/.test(npi)) {
      errors.npi = "NPI must be 10 digits";
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted fields");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    const errors: Record<string, string> = {};

    if (!legalName) {
      errors.legalName = "Legal name is required";
    }
    if (!addressStreet) {
      errors.addressStreet = "Street address is required";
    }
    if (!city) {
      errors.city = "City is required";
    }
    if (!state || state.length !== 2) {
      errors.state = "State is required (2 letters)";
    }
    if (!zipCode) {
      errors.zipCode = "ZIP code is required";
    }
    if (!phone) {
      errors.phone = "Phone is required";
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted fields");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError("");
    setFieldErrors({});

    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;

    if (currentStep === 2) {
      handleNpiLookup();
    } else if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setError("");
    setFieldErrors({});
    setCurrentStep(currentStep - 1);
  };

  const handleResendVerification = async () => {
    if (!userEmail || resendCooldown > 0) {
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      await resendVerification({ email: userEmail });

      setResendSuccess(true);
      // Set 60 second cooldown
      setResendCooldown(60);

      // Start countdown
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const errData = err as { error?: string };
      setError(errData.error || "Failed to resend verification email");
      console.error("Resend error:", err);
    } finally {
      setIsResending(false);
    }
  };

  const handleContinue = async () => {
    if (
      !surveySent &&
      (surveyVolume || surveyTracking || surveyLocations || surveyReferral)
    ) {
      try {
        await fetch(`${HUBSPOT_API_URL}/hubspot/contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: userEmail,
            pharmacyName: legalName || undefined,
            firstName: adminFirstName || undefined,
            lastName: adminLastName || undefined,
            phone: phone || undefined,
            state: state || undefined,
            ciiVolume: surveyVolume || undefined,
            trackingMethod: surveyTracking || undefined,
            locations: surveyLocations || undefined,
            referralSource: surveyReferral || undefined,
          }),
        });
      } catch {
        // silently fail
      }
      setSurveySent(true);
    }
    navigate({ to: autoLoginFailed ? "/login" : "/dashboard" });
  };

  const getPasswordStrength = (
    pw: string
  ): { score: number; label: string; color: string } => {
    if (!pw) return { score: 0, label: "", color: "bg-muted" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^a-zA-Z0-9]/.test(pw)) score++;

    if (score <= 1) return { score: 1, label: "Weak", color: "bg-destructive" };
    if (score <= 2) return { score: 2, label: "Fair", color: "bg-warning" };
    if (score <= 3) return { score: 3, label: "Good", color: "bg-secondary" };
    return { score: 4, label: "Strong", color: "bg-success" };
  };

  const passwordStrength = getPasswordStrength(password);

  if (success) {
    return (
      <div
        className="h-screen overflow-y-auto p-4 lg:p-6"
        style={{
          background:
            "linear-gradient(180deg, #070F1C 0%, #0F172A 50%, #1E293B 100%)",
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <div className="max-w-xl w-full my-4 lg:my-8 space-y-6">
            {/* Success header */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <CheckCircle2
                className="h-14 w-14 mx-auto mb-4"
                strokeWidth={2}
                style={{ color: "#479CA3" }}
              />
              <h2 className="text-2xl lg:text-3xl font-extrabold text-white mb-2">
                You&apos;re all set!
              </h2>
              <p
                className="text-sm lg:text-base mb-1"
                style={{ color: "rgba(148, 163, 184, 1)" }}
              >
                We sent a verification email to{" "}
                <span className="font-semibold text-white">{userEmail}</span>
              </p>
              <p
                className="text-xs"
                style={{ color: "rgba(100, 116, 139, 1)" }}
              >
                {autoLoginFailed
                  ? "Verify your email, then log in to continue."
                  : "Verify when you're ready — you're already signed in."}
              </p>
            </motion.div>

            {/* Optional questions */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="shadow-2xl border-0 rounded-3xl">
                <CardContent className="p-6 lg:p-8 space-y-5">
                  <p className="text-sm font-semibold text-foreground">
                    Help us tailor your experience
                  </p>

                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Daily dispense volume
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {DISPENSE_VOLUMES.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setSurveyVolume(v)}
                          className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                            surveyVolume === v
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-muted-foreground hover:border-primary/30"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Number of locations
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {LOCATION_COUNTS.map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setSurveyLocations(l)}
                          className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                            surveyLocations === l
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-muted-foreground hover:border-primary/30"
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      How do you currently track controlled substances?
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {TRACKING_METHODS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setSurveyTracking(m)}
                          className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                            surveyTracking === m
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-muted-foreground hover:border-primary/30"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      How did you hear about us?
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {REFERRAL_SOURCES.map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setSurveyReferral(r)}
                          className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                            surveyReferral === r
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-muted-foreground hover:border-primary/30"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleContinue}
                    className="w-full h-11 text-sm font-semibold shadow-lg shadow-primary/20 transition-all duration-200 hover:shadow-xl hover:shadow-primary/30"
                  >
                    {autoLoginFailed ? "Go to Login" : "Continue to Setup"}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Resend */}
            <div className="text-center">
              <span
                className="text-xs"
                style={{ color: "rgba(100, 116, 139, 1)" }}
              >
                Didn&apos;t receive the email?{" "}
              </span>
              <Button
                type="button"
                variant="link"
                onClick={handleResendVerification}
                disabled={isResending || resendCooldown > 0}
                className="p-0 h-auto text-xs font-medium"
                style={{ color: "#4A9FE8" }}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  "Resend Verification"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen overflow-y-auto p-4 lg:p-6"
      style={{
        background:
          "linear-gradient(180deg, #070F1C 0%, #0F172A 50%, #1E293B 100%)",
      }}
    >
      <div className="flex min-h-full items-center justify-center">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 max-w-6xl w-full my-4 lg:my-8 items-start">
          {/* Main Signup Card */}
          <div className="lg:col-span-3">
            <Card className="shadow-2xl border-0 overflow-hidden flex flex-col rounded-3xl">
              <CardHeader className="pb-3 pt-6 lg:pb-4 lg:pt-8 px-6 lg:px-8 flex-shrink-0">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="/logo-with-text-blue.png"
                    alt="C2 Vault"
                    className="h-6 w-auto"
                  />
                  <span className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground">
                    Controlled Substances. Fully Controlled.
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <CardTitle className="text-lg lg:text-xl font-semibold mb-1">
                      {
                        stepContent[currentStep as keyof typeof stepContent]
                          .title
                      }
                    </CardTitle>
                    <CardDescription className="text-xs lg:text-sm">
                      {
                        stepContent[currentStep as keyof typeof stepContent]
                          .description
                      }
                    </CardDescription>
                  </motion.div>
                </AnimatePresence>
              </CardHeader>
              <CardContent className="pt-0 px-6 lg:px-8 pb-4 lg:pb-6 overflow-y-auto flex-1">
                {/* Step Indicator */}
                <div className="mb-4 lg:mb-6">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4].map((s) => (
                      <div
                        key={s}
                        className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                          s <= currentStep ? "bg-primary" : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {error && (
                  <Alert
                    variant="destructive"
                    className="mb-3 lg:mb-4 border-destructive/50 py-2"
                  >
                    <AlertDescription className="text-xs lg:text-sm">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="min-h-[340px]">
                  <AnimatePresence mode="wait">
                    {/* Step 1: Email & Password */}
                    {currentStep === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="space-y-3 lg:space-y-4"
                      >
                        <div className="bg-primary/5 border border-primary/10 rounded-xl p-3 text-xs lg:text-sm">
                          <p className="font-semibold text-foreground mb-1">
                            Setting Up Your Pharmacy Account
                          </p>
                          <p className="text-muted-foreground leading-relaxed">
                            This email and password will be used to log in to
                            your pharmacy's C2 Vault account.
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          <Label
                            htmlFor="adminEmail"
                            className="text-xs lg:text-sm font-medium"
                          >
                            Email Address
                          </Label>
                          <Input
                            id="adminEmail"
                            type="email"
                            placeholder="pharmacy@example.com"
                            value={adminEmail}
                            onChange={(e) => setAdminEmail(e.target.value)}
                            required
                            disabled={isLoading}
                            className={`h-9 lg:h-10 text-sm transition-all duration-200 ${
                              fieldErrors.adminEmail
                                ? "border-destructive focus-visible:ring-destructive"
                                : ""
                            }`}
                          />
                          {fieldErrors.adminEmail && (
                            <p className="text-xs text-destructive">
                              {fieldErrors.adminEmail}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label
                            htmlFor="password"
                            className="text-xs lg:text-sm font-medium"
                          >
                            Create Password
                          </Label>
                          <div className="relative" {...capsLockProps}>
                            <Input
                              id="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              disabled={isLoading}
                              className={`h-9 lg:h-10 text-sm pr-9 transition-all duration-200 ${
                                fieldErrors.password
                                  ? "border-destructive focus-visible:ring-destructive"
                                  : ""
                              }`}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowPassword((v) => !v)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <CapsLockIndicator isCapsLockOn={isCapsLockOn} />
                          {password && (
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1 flex-1">
                                {[1, 2, 3, 4].map((i) => (
                                  <div
                                    key={i}
                                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                                      i <= passwordStrength.score
                                        ? passwordStrength.color
                                        : "bg-muted"
                                    }`}
                                  />
                                ))}
                              </div>
                              <span
                                className={`text-[10px] font-medium ${
                                  passwordStrength.score <= 1
                                    ? "text-danger"
                                    : passwordStrength.score <= 2
                                      ? "text-warning-hover"
                                      : passwordStrength.score <= 3
                                        ? "text-primary"
                                        : "text-success"
                                }`}
                              >
                                {passwordStrength.label}
                              </span>
                            </div>
                          )}
                          {fieldErrors.password ? (
                            <p className="text-xs text-destructive">
                              {fieldErrors.password}
                            </p>
                          ) : (
                            !password && (
                              <p className="text-xs text-muted-foreground">
                                At least 8 characters and one uppercase letter
                              </p>
                            )
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label
                            htmlFor="confirmPassword"
                            className="text-xs lg:text-sm font-medium"
                          >
                            Confirm Password
                          </Label>
                          <div className="relative" {...capsLockProps}>
                            <Input
                              id="confirmPassword"
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="••••••••"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              required
                              disabled={isLoading}
                              className={`h-9 lg:h-10 text-sm pr-9 transition-all duration-200 ${
                                fieldErrors.confirmPassword
                                  ? "border-destructive focus-visible:ring-destructive"
                                  : ""
                              }`}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowConfirmPassword((v) => !v)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <CapsLockIndicator isCapsLockOn={isCapsLockOn} />
                          {fieldErrors.confirmPassword && (
                            <p className="text-xs text-destructive">
                              {fieldErrors.confirmPassword}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Step 2: NPI */}
                    {currentStep === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="space-y-3 lg:space-y-4"
                      >
                        <div className="bg-primary/5 border border-primary/10 rounded-xl p-3 text-xs lg:text-sm">
                          <p className="font-semibold text-foreground mb-1">
                            Enter Your Pharmacy's NPI
                          </p>
                          <p className="text-muted-foreground leading-relaxed">
                            We'll use your 10-digit National Provider Identifier
                            to automatically fill in your pharmacy's
                            information.
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          <Label
                            htmlFor="npi"
                            className="text-xs lg:text-sm font-medium"
                          >
                            NPI Number
                          </Label>
                          <Input
                            id="npi"
                            type="text"
                            placeholder="1234567890"
                            value={npi}
                            onChange={(e) =>
                              setNpi(e.target.value.replace(/\D/g, ""))
                            }
                            maxLength={10}
                            required
                            disabled={npiLoading}
                            className={`h-10 lg:h-11 font-mono text-base lg:text-lg tracking-wider transition-all duration-200 ${
                              fieldErrors.npi
                                ? "border-destructive focus-visible:ring-destructive"
                                : ""
                            }`}
                          />
                          {fieldErrors.npi ? (
                            <p className="text-xs text-destructive">
                              {fieldErrors.npi}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Don't know your NPI?{" "}
                              <a
                                href="https://npiregistry.cms.hhs.gov/search"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline font-medium"
                              >
                                Look it up here
                              </a>
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Step 3: Pharmacy Details */}
                    {currentStep === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="space-y-3"
                      >
                        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-2.5 text-xs flex items-start gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          <p className="text-muted-foreground">
                            Pharmacy information loaded - review and complete
                            below
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                          {/* Row 1: Legal Name, DBA Name */}
                          <div className="space-y-1">
                            <Label
                              htmlFor="legalName"
                              className="text-xs font-medium"
                            >
                              Legal Name *
                            </Label>
                            <Input
                              id="legalName"
                              value={legalName}
                              onChange={(e) => setLegalName(e.target.value)}
                              required
                              className={`h-8 text-sm ${
                                fieldErrors.legalName
                                  ? "border-destructive focus-visible:ring-destructive"
                                  : ""
                              }`}
                            />
                            {fieldErrors.legalName && (
                              <p className="text-xs text-destructive">
                                {fieldErrors.legalName}
                              </p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <Label
                              htmlFor="dbaName"
                              className="text-xs font-medium"
                            >
                              DBA Name
                            </Label>
                            <Input
                              id="dbaName"
                              value={dbaName}
                              onChange={(e) => setDbaName(e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>

                          {/* Row 2: Address 1, Address 2 */}
                          <div className="space-y-1">
                            <Label
                              htmlFor="addressStreet"
                              className="text-xs font-medium"
                            >
                              Street Address *
                            </Label>
                            <Input
                              id="addressStreet"
                              value={addressStreet}
                              onChange={(e) => setAddressStreet(e.target.value)}
                              required
                              className={`h-8 text-sm ${
                                fieldErrors.addressStreet
                                  ? "border-destructive focus-visible:ring-destructive"
                                  : ""
                              }`}
                            />
                            {fieldErrors.addressStreet && (
                              <p className="text-xs text-destructive">
                                {fieldErrors.addressStreet}
                              </p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <Label
                              htmlFor="addressStreet2"
                              className="text-xs font-medium"
                            >
                              Address Line 2
                            </Label>
                            <Input
                              id="addressStreet2"
                              value={addressStreet2}
                              onChange={(e) =>
                                setAddressStreet2(e.target.value)
                              }
                              className="h-8 text-sm"
                            />
                          </div>

                          {/* Row 3: City, State */}
                          <div className="space-y-1">
                            <Label
                              htmlFor="city"
                              className="text-xs font-medium"
                            >
                              City *
                            </Label>
                            <Input
                              id="city"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              required
                              className={`h-8 text-sm ${
                                fieldErrors.city
                                  ? "border-destructive focus-visible:ring-destructive"
                                  : ""
                              }`}
                            />
                            {fieldErrors.city && (
                              <p className="text-xs text-destructive">
                                {fieldErrors.city}
                              </p>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label
                                htmlFor="state"
                                className="text-xs font-medium"
                              >
                                State *
                              </Label>
                              <Input
                                id="state"
                                value={state}
                                onChange={(e) =>
                                  setState(e.target.value.toUpperCase())
                                }
                                maxLength={2}
                                required
                                className={`h-8 text-sm uppercase ${
                                  fieldErrors.state
                                    ? "border-destructive focus-visible:ring-destructive"
                                    : ""
                                }`}
                              />
                              {fieldErrors.state && (
                                <p className="text-xs text-destructive">
                                  {fieldErrors.state}
                                </p>
                              )}
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="zipCode"
                                className="text-xs font-medium"
                              >
                                ZIP *
                              </Label>
                              <Input
                                id="zipCode"
                                value={zipCode}
                                onChange={(e) => setZipCode(e.target.value)}
                                required
                                className={`h-8 text-sm ${
                                  fieldErrors.zipCode
                                    ? "border-destructive focus-visible:ring-destructive"
                                    : ""
                                }`}
                              />
                              {fieldErrors.zipCode && (
                                <p className="text-xs text-destructive">
                                  {fieldErrors.zipCode}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Row 4: Phone, Timezone */}
                          <div className="space-y-1">
                            <Label
                              htmlFor="phone"
                              className="text-xs font-medium"
                            >
                              Phone *
                            </Label>
                            <Input
                              id="phone"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              required
                              className={`h-8 text-sm ${
                                fieldErrors.phone
                                  ? "border-destructive focus-visible:ring-destructive"
                                  : ""
                              }`}
                            />
                            {fieldErrors.phone && (
                              <p className="text-xs text-destructive">
                                {fieldErrors.phone}
                              </p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <Label
                              htmlFor="timezone"
                              className="text-xs font-medium"
                            >
                              Timezone
                            </Label>
                            <Select
                              value={timezone}
                              onValueChange={setTimezone}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="America/New_York">
                                  Eastern
                                </SelectItem>
                                <SelectItem value="America/Chicago">
                                  Central
                                </SelectItem>
                                <SelectItem value="America/Denver">
                                  Mountain
                                </SelectItem>
                                <SelectItem value="America/Los_Angeles">
                                  Pacific
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 4: Review */}
                    {currentStep === 4 && (
                      <motion.div
                        key="step4"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="space-y-3"
                      >
                        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Account
                          </p>
                          <div className="text-sm">
                            <span className="text-muted-foreground">
                              Email:
                            </span>{" "}
                            <span className="font-medium">{adminEmail}</span>
                          </div>
                        </div>

                        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Pharmacy
                          </p>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                            <div>
                              <span className="text-muted-foreground">
                                Name:
                              </span>{" "}
                              <span className="font-medium">{legalName}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">
                                NPI:
                              </span>{" "}
                              <span className="font-medium">{npi}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-muted-foreground">
                                Address:
                              </span>{" "}
                              <span className="font-medium">
                                {addressStreet}, {city}, {state} {zipCode}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">
                                Phone:
                              </span>{" "}
                              <span className="font-medium">{phone}</span>
                            </div>
                          </div>
                        </div>

                        <p className="text-[10px] lg:text-xs text-muted-foreground text-center leading-relaxed">
                          By registering, you agree to the{" "}
                          <a
                            href="https://c2vault.com/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            Terms of Use
                          </a>{" "}
                          and{" "}
                          <a
                            href="https://c2vault.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            Privacy Policy
                          </a>
                          .
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center mt-4 lg:mt-5 pt-3 lg:pt-4 border-t flex-shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    disabled={currentStep === 1 || isLoading || npiLoading}
                    className="h-9 px-3 text-sm"
                  >
                    <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                    Back
                  </Button>

                  {currentStep < 4 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={isLoading || npiLoading}
                      className="h-9 px-5 text-sm"
                    >
                      {npiLoading ? (
                        <>
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          Looking up...
                        </>
                      ) : (
                        <>
                          Next
                          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className="h-9 px-5 text-sm"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        "Complete Registration"
                      )}
                    </Button>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t text-center text-xs lg:text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-medium text-primary hover:underline transition-colors"
                  >
                    Sign in
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar — trust signals */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hidden lg:flex lg:col-span-2 flex-col gap-6"
          >
            {/* What's included */}
            <div
              className="rounded-2xl p-6"
              style={{
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <h3 className="text-base font-semibold text-white mb-4">
                Your free trial includes
              </h3>
              <ul className="space-y-3">
                {[
                  "Unlimited controlled substance logging",
                  "Full chain-of-custody audit trail",
                  "DEA-ready report generation",
                  "NDC auto-lookup",
                  "Staff access controls",
                  "Dedicated onboarding support",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check
                      className="shrink-0 mt-0.5"
                      size={16}
                      style={{ color: "#479CA3" }}
                    />
                    <span
                      className="text-sm"
                      style={{ color: "rgba(203, 213, 225, 1)" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust signals */}
            <div className="space-y-4">
              {[
                {
                  icon: Shield,
                  title: "HIPAA Compliant",
                  body: "Your pharmacy data is encrypted and protected to the highest standards.",
                },
                {
                  icon: Clock,
                  title: "14-Day Free Trial",
                  body: "Try every feature risk-free. No credit card required to start.",
                },
                {
                  icon: FileCheck,
                  title: "Setup in Minutes",
                  body: "No hardware to install. Sign up and start logging in under 5 minutes.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 rounded-xl p-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: "rgba(31, 95, 168, 0.15)" }}
                    >
                      <Icon size={18} style={{ color: "#4A9FE8" }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {item.title}
                      </p>
                      <p
                        className="text-xs mt-0.5"
                        style={{ color: "rgba(148, 163, 184, 1)" }}
                      >
                        {item.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Testimonial */}
            <div
              className="rounded-2xl p-5"
              style={{
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <p
                className="text-sm leading-relaxed italic"
                style={{ color: "rgba(203, 213, 225, 0.9)" }}
              >
                &ldquo;C2 Vault eliminated the frustration of dealing with
                handwritten CII logs. Everything is organized, clear, and takes
                seconds to enter.&rdquo;
              </p>
              <div className="mt-3 flex items-center gap-2.5">
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                  style={{ background: "rgba(31, 95, 168, 0.3)" }}
                >
                  BR
                </div>
                <div>
                  <p className="text-xs font-medium text-white">Basil Romhen</p>
                  <p
                    className="text-[10px]"
                    style={{ color: "rgba(100, 116, 139, 1)" }}
                  >
                    Supervising Pharmacist, Bliss Drugs
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
